'use client';

import {
	Checkbox, SimpleGrid, Stack,
} from '@chakra-ui/react';
import { z } from 'zod';

export const NestedCategorySchema = z.object({
	id:       z.string(),
	children: z.array(z.lazy((): z.ZodTypeAny => NestedCategorySchema)).optional(),
	label:    z.string(),
});

export type NestedCategory = z.infer<typeof NestedCategorySchema>;

type NestedCheckboxProps = {
	level?:      number
	node:        NestedCategory
	selectedIds: string[]

	onSelect: (id: string, checked: boolean) => void
};

export const NestedCheckbox = ({
	level = 0, node, onSelect, selectedIds,
}: NestedCheckboxProps) => {
	const children = node.children ?? [];
	const childIds = children.map((c: unknown) => (c as { id: string }).id);
	const selectedChildrenCount = childIds.filter(id => selectedIds.includes(id)).length;

	const allChildrenChecked = children.length > 0 && selectedChildrenCount === children.length;
	const someChildrenChecked = selectedChildrenCount > 0 && selectedChildrenCount < children.length;

	const checkedBySelf = selectedIds.includes(node.id);

	const checked = checkedBySelf || allChildrenChecked;
	const isIndeterminate = !checkedBySelf && someChildrenChecked;
	const isRoot = level === 0;

	return (
		<Stack flex={1}>
			<Checkbox.Root
				borderBottom="1px solid"
				borderBottomColor="gray.15"
				checked={isIndeterminate ? 'indeterminate' : checked}
				onCheckedChange={e => onSelect(node.id, e.checked === true)}
				px={6}
				py={4}
			>
				<Checkbox.HiddenInput />
				<Checkbox.Control>
					<Checkbox.Indicator />
				</Checkbox.Control>
				<Checkbox.Label textStyle={isRoot ? 'bodyBold' : 'bodySmall'}>
					{node.label}
				</Checkbox.Label>
			</Checkbox.Root>

			{children.length > 0 && (
				<SimpleGrid
					columns={1}
					gap={0}
					pl={8}
				>
					{children.map((child: unknown) => (
						<NestedCheckbox
							key={(child as { id: string }).id}
							level={level + 1}
							node={child as NestedCategory}
							onSelect={onSelect}
							selectedIds={selectedIds}
						/>
					))}
				</SimpleGrid>
			)}
		</Stack>
	);
};
